import pcURL from '@planningcenter/url';
import React, { useMemo } from 'react';
import apiRequest, { railsCsrfFetch, defaultFetch } from '../chat-core/api_request.js';

const ChatApiContext = React.createContext({});
const buildRelationships = (relationships = {}) => {
    return Object.keys(relationships).reduce((acc, key) => {
        const { data } = relationships[key];
        const isArray = Array.isArray(data);
        return {
            ...acc,
            [`${key}_${isArray ? "ids" : "id"}`]: isArray
                ? data.map(({ id }) => id)
                : data === null || data === undefined ? undefined : data.id,
        };
    }, {});
};
const buildResponse = (json) => {
    if (json === null)
        return null;
    const data = [json.data].flat();
    const [head, ...tail] = data.map(({ id, attributes, relationships }) => ({
        id,
        ...buildRelationships(relationships),
        ...attributes,
    }));
    return Array.isArray(json.data) ? (head ? [head, ...tail] : []) : head;
};
const buildApi = ({ app, env }) => {
    const url = `${pcURL(env)("api")}/${app}/v2`;
    return {
        delete: (path) => apiRequest(railsCsrfFetch, [url, path].join("/"), { method: "DELETE" }),
        get: async (path, { fields = {}, ...query } = {}, { unpaginated = false } = {}) => {
            var _a, _b;
            fields = Object.entries(fields).reduce((acc, [key, value]) => ({ ...acc, [`fields[${key}]`]: value }), {});
            const params = new URLSearchParams({ ...fields, ...query });
            const { json } = await apiRequest(defaultFetch, [[url, path].join("/"), params].join("?"));
            let results = buildResponse(json);
            let next = (_a = json === null || json === undefined ? undefined : json.links) === null || _a === undefined ? undefined : _a.next;
            while (unpaginated && next) {
                const { json } = await apiRequest(defaultFetch, next);
                results = [...results, ...buildResponse(json)];
                next = (_b = json === null || json === undefined ? undefined : json.links) === null || _b === undefined ? undefined : _b.next;
            }
            return results;
        },
        patch: async (path, attributes) => {
            const { json } = await apiRequest(railsCsrfFetch, [url, path].join("/"), {
                data: { data: { attributes } },
                method: "PATCH",
            });
            return buildResponse(json);
        },
        post: async (path, attributes) => {
            const { json } = await apiRequest(railsCsrfFetch, [url, path].join("/"), {
                data: { data: { attributes } },
                method: "POST",
            });
            return buildResponse(json);
        },
    };
};
const ChatApiProvider = ({ children, env }) => {
    const value = useMemo(() => {
        const chat = buildApi({ app: "chat", env });
        const services = buildApi({ app: "services", env });
        const groups = buildApi({ app: "groups", env });
        return { chat, groups, services };
    }, [env]);
    return (React.createElement(ChatApiContext.Provider, { value: value }, children));
};

export { ChatApiContext, ChatApiProvider };
//# sourceMappingURL=chat_api_context.js.map
